import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createClientAddressList, editClientCompany } from '@/api/client';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { AddressFieldInput } from '@/components/form/shared/AddressField';
import { JOBS } from '@/constants/routes';
import { useApiMutation } from '@/hooks/api';
import { useLocationReferenceData } from '@/hooks/queries/useReferenceData';
import { useApiFeedback } from '@/hooks/useApiFeedback';
import { useRefreshSessionUser } from '@/hooks/useRefreshSessionUser';
import { useUrlState } from '@/hooks/useUrlState';
import { tCatalog } from '@/i18n/tCatalog';
import { PageTitle } from '@/UI/typography';
import { getLocalStorageItem } from '@/utils/localStorage';
import { composeClientOnboardForm } from '@/utils/validation/formSchemas/clientSchemas';

import type { Company } from '@/types/company';
import type { User } from '@/types/user';

type ClientOnboardProps = {
  user: User & { company: Company };
};

type OnboardVariables = {
  values: Record<string, unknown>;
  officeLocations: { id: string; address: string }[];
};

const ClientOnboard = ({ user }: ClientOnboardProps) => {
  const { company } = user;
  const refreshSessionUser = useRefreshSessionUser();
  const [form] = Form.useForm();
  const { history } = useUrlState();
  const { data: locationReference } = useLocationReferenceData();
  const regions = useMemo(
    () => locationReference?.regions ?? [],
    [locationReference?.regions],
  );
  const industries = useMemo(
    () => locationReference?.industries ?? [],
    [locationReference?.industries],
  );
  const [logo, setLogo] = useState<string>(company.image ?? '');
  const [fileError, setFileError] = useState<string>('');
  const [address, setAddress] = useState(company.address ?? '');
  const { t } = useTranslation();
  const { showErrorFrom } = useApiFeedback();
  const hydratedCompanyIdRef = useRef<string | null>(null);

  const handleInvalidImageFormat = useCallback(() => {
    setFileError(t('FILE_FORMAT_VALIDATION'));
  }, [t]);

  const handleFormValuesChange = useCallback(
    (changedValues: Record<string, unknown>) => {
      if (typeof changedValues.image === 'string') {
        setLogo(changedValues.image);
        setFileError('');
      }
    },
    [],
  );

  const industryOptions = useMemo(
    () =>
      industries.map((industry) => ({
        value: industry.id,
        label: tCatalog('industries', industry.name),
      })),
    [industries],
  );

  const regionOptions = useMemo(
    () =>
      regions.map((region) => ({
        value: region.id,
        label: tCatalog('regions', region.name),
      })),
    [regions],
  );

  const addressComponent = useMemo(
    () => (
      <AddressFieldInput
        address={address}
        setAddress={setAddress}
        validating={false}
      />
    ),
    [address],
  );

  const composedForm = useMemo(
    () =>
      composeClientOnboardForm(t, {
        industryOptions,
        regionOptions,
        companyId: company.id,
        includeAddressField: true,
        addressComponent,
      }),
    [t, industryOptions, regionOptions, company.id, addressComponent],
  );

  const sections = useMemo(
    () => [
      {
        key: 'logo',
        fields: [
          {
            name: 'image',
            type: 'image' as const,
            label: t('COMPANY_LOGO'),
            help: fileError || undefined,
            validateStatus: fileError ? ('error' as const) : undefined,
            componentProps: {
              name: t('COMPANY_LOGO'),
              onInvalidFormat: handleInvalidImageFormat,
            },
          },
        ],
      },
      ...composedForm.sections,
    ],
    [t, fileError, handleInvalidImageFormat, composedForm.sections],
  );

  useEffect(() => {
    if (hydratedCompanyIdRef.current === company.id) return;
    hydratedCompanyIdRef.current = company.id;

    form.setFieldsValue({
      company_name: company.name,
      city: company.city?.name,
      industry: company.industry?.id,
      region: company.region?.id,
      notable_clients: company.notable_clients,
      number_of_employees: company.number_of_employees,
      average_age: company.average_age,
      male_percentage: company.male_percentage,
      dress_code: company.dress_code,
      description: company.description,
      difference_description: company.difference_description,
      image: company.image,
      office_locations: company.office_locations?.length
        ? company.office_locations
        : [],
    });
  }, [company, form]);

  const onboardMutation = useApiMutation<void, OnboardVariables>({
    mutationFn: async ({ values, officeLocations }) => {
      await createClientAddressList(company.id, officeLocations);
      await editClientCompany({ ...values, address }, company.id);
    },
    successKey: 'INFORMATION_SAVE_SUCCESS',
    successDuration: 5,
    errorKey: 'FAILED_TO_UPDATE_INFORMATION',
    onSuccess: async () => {
      await refreshSessionUser();
      const jobId = getLocalStorageItem('job');
      if (jobId) {
        history.push(JOBS + `/edit/${jobId}/info`);
      } else {
        history.push(JOBS);
      }
    },
    onError: (error) => {
      showErrorFrom(error);
    },
  });

  const onFinish = (values: Record<string, unknown>) => {
    if (logo.startsWith('http')) {
      delete values.image;
    } else if (!logo) {
      delete values.image;
    }

    const officeLocations =
      (form.getFieldValue('office_locations') as
        { id: string; address: string }[] | undefined) ?? [];

    onboardMutation.mutate({ values, officeLocations });
  };

  return (
    <>
      <PageTitle>{t('COMPANY_INFO_TITLE')}</PageTitle>
      <SchemaForm
        form={form}
        formSchema={composedForm.formSchema}
        name="client-onboard"
        scrollToFirstError
        onFinish={onFinish}
        onValuesChange={(changedValues) => {
          handleFormValuesChange(changedValues);
          if (
            changedValues.male_percentage &&
            (changedValues.male_percentage as number) > 100
          ) {
            form.setFieldsValue({ male_percentage: 100 });
          }
        }}
        sections={sections}
        footer={{
          actions: [
            {
              label: t('NEXT'),
              htmlType: 'submit',
              type: 'primary',
              loading: onboardMutation.loading,
            },
          ],
        }}
      />
    </>
  );
};

export default ClientOnboard;
